"use client"

import { motion } from "framer-motion"
import { useUser } from "@stackframe/stack"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import Link from "next/link"
import {
  TrendingUp,
  Clock,
  Target,
  Award,
  BookOpen,
  Users,
  Star,
  CheckCircle,
  ArrowRight
} from "lucide-react"

// 用户学习数据展示组件（已登录用户）
const UserLearningDashboard = () => {
  // 这里应该从API获取真实数据，现在使用模拟数据
  const learningStats = {
    todayPractice: 15,
    weeklyGoal: 100,
    weeklyProgress: 65,
    streak: 7,
    totalQuestions: 1250,
    correctRate: 78,
    rank: 156,
    recentSubjects: [
      { name: "系统架构设计师", progress: 65, lastStudy: "今天" },
      { name: "信息系统项目管理师", progress: 23, lastStudy: "3天前" }
    ]
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* 今日学习 */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-orange-500" />
            今日学习
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">已练习题目</span>
              <span className="font-semibold">{learningStats.todayPractice}题</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">连续学习</span>
              <Badge variant="secondary">{learningStats.streak}天</Badge>
            </div>
            <Button asChild className="w-full" size="sm">
              <Link href="/questions/daily">继续今日练习</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 本周目标 */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-blue-500" />
            本周目标
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">进度</span>
              <span className="font-semibold">{learningStats.weeklyProgress}/{learningStats.weeklyGoal}</span>
            </div>
            <Progress value={(learningStats.weeklyProgress / learningStats.weeklyGoal) * 100} className="h-2" />
            <p className="text-xs text-muted-foreground">
              还需 {learningStats.weeklyGoal - learningStats.weeklyProgress} 题完成本周目标
            </p>
          </div>
        </CardContent>
      </Card>

      {/* 学习成就 */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Award className="h-5 w-5 text-purple-500" />
            学习成就
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">总题数</span>
              <span className="font-semibold">{learningStats.totalQuestions.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">正确率</span>
              <Badge variant={learningStats.correctRate >= 80 ? "default" : "secondary"}>
                {learningStats.correctRate}%
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">排名</span>
              <span className="font-semibold">#{learningStats.rank}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 最近学习科目 */}
      <Card className="border-0 shadow-lg md:col-span-2 lg:col-span-3">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="h-5 w-5 text-green-500" />
            最近学习科目
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {learningStats.recentSubjects.map((subject, index) => (
              <div key={index} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">{subject.name}</h4>
                  <span className="text-sm text-muted-foreground">{subject.lastStudy}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">学习进度</span>
                    <span className="text-sm font-medium">{subject.progress}%</span>
                  </div>
                  <Progress value={subject.progress} className="h-2" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Button variant="outline" asChild>
              <Link href="/subjects">
                查看所有科目 <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// 智能学习体验展示组件（未登录用户）
const SmartLearningShowcase = () => {
  const learningFeatures = [
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
      title: "AI智能推荐",
      description: "基于学习进度和能力分析，智能推荐最适合的学习内容",
      features: ["个性化题目推荐", "弱项智能识别", "学习路径优化"],
      color: "blue"
    },
    {
      icon: <Target className="h-8 w-8 text-green-500" />,
      title: "自适应学习",
      description: "根据答题情况动态调整难度，确保学习效果最大化",
      features: ["难度自动调节", "学习节奏控制", "知识点巩固"],
      color: "green"
    },
    {
      icon: <Clock className="h-8 w-8 text-purple-500" />,
      title: "科学学习计划",
      description: "基于考试时间和个人情况，制定最优学习计划",
      features: ["时间智能分配", "进度实时跟踪", "计划动态调整"],
      color: "purple"
    }
  ]

  const technologyHighlights = [
    {
      icon: <Award className="h-6 w-6 text-orange-500" />,
      title: "智能错题分析",
      description: "深度分析错题原因，提供针对性解决方案"
    },
    {
      icon: <BookOpen className="h-6 w-6 text-indigo-500" />,
      title: "知识图谱构建",
      description: "构建完整知识体系，帮助理解知识点关联"
    },
    {
      icon: <Users className="h-6 w-6 text-teal-500" />,
      title: "学习社区互动",
      description: "与同学交流讨论，分享学习心得和经验"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-pink-500" />,
      title: "实时学习反馈",
      description: "即时获得学习反馈，及时调整学习策略"
    }
  ]

  return (
    <div className="space-y-16">
      {/* 核心学习特色 */}
      <div>
        <h3 className="text-2xl font-bold text-center mb-8">智能化学习体验</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {learningFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border-0 shadow-lg h-full hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-lg bg-${feature.color}-50 dark:bg-${feature.color}-950`}>
                      {feature.icon}
                    </div>
                    <h4 className="font-semibold text-lg">{feature.title}</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">{feature.description}</p>
                  <div className="space-y-2">
                    {feature.features.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <div className={`w-1.5 h-1.5 rounded-full bg-${feature.color}-500`} />
                        <span className="text-muted-foreground">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 技术亮点 */}
      <div>
        <h3 className="text-2xl font-bold text-center mb-8">技术驱动的学习创新</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {technologyHighlights.map((highlight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-6 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-300"
            >
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-white dark:bg-slate-900 shadow-md">
                  {highlight.icon}
                </div>
              </div>
              <h4 className="font-semibold mb-2">{highlight.title}</h4>
              <p className="text-sm text-muted-foreground">{highlight.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 学习方法对比 */}
      <div>
        <h3 className="text-2xl font-bold text-center mb-8">传统学习 vs 智能学习</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* 传统学习 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800"
          >
            <h4 className="font-semibold text-red-700 dark:text-red-300 mb-4 flex items-center gap-2">
              <span className="text-xl">📚</span>
              传统学习方式
            </h4>
            <ul className="space-y-2 text-sm text-red-600 dark:text-red-400">
              <li>• 盲目刷题，缺乏针对性</li>
              <li>• 学习计划固化，难以调整</li>
              <li>• 错题重复出现，效率低下</li>
              <li>• 缺乏学习反馈，进步缓慢</li>
              <li>• 孤立学习，缺少交流</li>
            </ul>
          </motion.div>

          {/* 智能学习 */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800"
          >
            <h4 className="font-semibold text-green-700 dark:text-green-300 mb-4 flex items-center gap-2">
              <span className="text-xl">🚀</span>
              智能学习体验
            </h4>
            <ul className="space-y-2 text-sm text-green-600 dark:text-green-400">
              <li>• AI推荐精准题目，高效提升</li>
              <li>• 动态调整计划，适应个人节奏</li>
              <li>• 智能错题分析，避免重复错误</li>
              <li>• 实时学习反馈，持续优化</li>
              <li>• 社区互动学习，共同进步</li>
            </ul>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export const PersonalizedContentSection = () => {
  const user = useUser()

  return (
    <section className="w-full py-20 md:py-28">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {user ? "您的学习概览" : "智能化学习新体验"}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {user
              ? "查看您的学习进度和成就，继续您的备考之旅"
              : "运用AI技术和科学方法，为您打造个性化的高效学习体验"
            }
          </p>
        </div>

        {user ? <UserLearningDashboard /> : <SmartLearningShowcase />}
      </div>
    </section>
  )
}
